import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft } from "lucide-react";
import { API, useAuth } from "@/App";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      // Read body exactly once as text so chained hooks (sw, devtools) never
      // double-consume the stream.
      const raw = await response.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }

      if (!response.ok) {
        throw new Error(
          (typeof data.detail === "string" && data.detail) || "Login failed"
        );
      }

      login(data);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuest = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/auth/guest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const raw = await response.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
      if (!response.ok) {
        throw new Error(
          (typeof data.detail === "string" && data.detail) || "Guest sign-in failed"
        );
      }
      login(data);
      toast.success("Welcome! You're in as a guest.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      {/* Background */}
      <div className="absolute inset-0 bg-islamic-blue-950">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1676607185077-f6f289c669e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxpc2xhbWljJTIwZ2VvbWV0cmljJTIwcGF0dGVybiUyMGdvbGQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Njg1MDYyOTN8MA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-slate-400 hover:text-gold-400 mb-8 font-outfit transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        {/* Card */}
        <div className="glass rounded-2xl p-8 gold-glow">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/hifz-logo.png"
              alt="Hifz logo"
              className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg"
              data-testid="app-logo"
            />
            <h1 className="font-cinzel text-2xl font-bold text-slate-100">Welcome Back</h1>
            <p className="text-slate-400 font-outfit mt-2">Continue your memorization journey</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300 font-outfit">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="email-input"
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300 font-outfit">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  data-testid="password-input"
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 focus:border-gold-400/50 rounded-lg text-slate-200 placeholder:text-slate-600"
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full btn-gold rounded-full py-6 font-outfit font-medium"
            >
              {isLoading ? (
                <div className="spinner w-5 h-5"></div>
              ) : (
                "Sign In"
              )}
            </Button>

            <div className="text-right -mt-2">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-slate-400 hover:text-gold-400 text-sm font-outfit"
              >
                Forgot password?
              </Link>
            </div>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 bg-islamic-blue-900 text-slate-500 font-outfit">or</span>
            </div>
          </div>

          {/* Guest */}
          <Button
            data-testid="guest-signin-btn"
            type="button"
            onClick={handleGuest}
            disabled={isLoading}
            variant="outline"
            className="w-full rounded-full py-6 font-outfit border-white/15 text-slate-200 hover:bg-white/5 hover:text-gold-400"
          >
            Continue as Guest
          </Button>
          <p className="text-center text-slate-600 text-xs font-outfit mt-2">
            Your progress saves for this device. Upgrade to a full account anytime.
          </p>

          {/* Register Link */}
          <p className="text-center mt-6 text-slate-400 font-outfit">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-gold-400 hover:text-gold-300 transition-colors">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
